'use client';

import { useId, type ReactNode } from 'react';

import type { OrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import { CARD, TEXT_LINK } from '@eliadmin/core/src/components/nt/account/orders/order-parts';
import type { OrderStage } from '@eliadmin/core/src/components/nt/account/orders/order-stage';
import { useOrderTexts } from '@eliadmin/core/src/components/nt/account/orders/order-texts';
import { orderExtras } from '@eliadmin/core/src/components/nt/account/orders/orders-data';
import { Icon, type IconName } from '@eliadmin/core/src/components/nt/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { formatPhone, joinStreet } from '@eliadmin/core/src/utils/nt/address';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type Country = OrderInfo['country'] | null | undefined;

/** „080 01 Prešov, Slovenská republika“. */
function placeLine(postcode: string | null, city: string | null, country: Country) {
  const place = [postcode, city].filter(Boolean).join(' ');
  return [place, country?.name].filter(Boolean).join(', ');
}

/**
 * Figma „Info · …“ (208:12024): 20 px green icon and Body/L Bold title, Body/M #363D36 lines,
 * 8 px apart; padding 20 / 24, radius 16.
 */
function InfoCard({
  icon,
  title,
  lines,
  children,
}: {
  icon: IconName;
  title: string;
  lines: string[];
  children?: ReactNode;
}) {
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className={cn(CARD, 'flex min-w-0 flex-col gap-2 p-4 md:px-6 md:py-5')}
    >
      <h2 id={titleId} className="nt-body-l-bold flex items-center gap-2 text-[#0D110D]">
        <Icon name={icon} size={20} className="text-[#127A26]" />
        {title}
      </h2>
      {lines.filter(Boolean).map((line, index) => (
        <p key={index} className="nt-body-m text-[#363D36] [overflow-wrap:anywhere]">
          {line}
        </p>
      ))}
      {children}
    </section>
  );
}

/**
 * Figma „Info grid“ (208:12024): contact, delivery, payment and billing address, 2 × 2 from a
 * 40rem column. From `/orders/info`; what it does not send is not shown („Visa •••• 4821“ – no card
 * data in the API). A paid order gets „· zaplatené“ („Konto - zaplatené“); an unpaid one with a
 * payment link the core „Zaplatiť objednávku“.
 */
export function OrderInfoCards({ info, stage }: { info: OrderInfo; stage: OrderStage }) {
  const { t } = useTranslations();
  const { account } = useOrderTexts();
  const extras = orderExtras(info);
  const name = [info.name, info.surname].filter(Boolean).join(' ');
  const delivery = info.different_delivery_address
    ? [
        extras.recipientCompany,
        extras.recipient,
        joinStreet(info.address2, info.street_number2),
        placeLine(info.postcode2, info.city2, info.country2),
      ]
    : [
        name,
        joinStreet(info.address, info.street_number),
        placeLine(info.postcode, info.city, info.country),
      ];
  const document = info.documents?.[0];
  const payable = !info.paid && !!info.payment_url && stage !== 'cancelled';

  return (
    <div className="grid gap-4 @[40rem]:grid-cols-2">
      <InfoCard
        icon="user"
        title={t('text360')}
        lines={[name, info.email, formatPhone(extras.phone)]}
      />
      <InfoCard
        icon="truck"
        title={[t('text3497'), info.shipment?.name].filter(Boolean).join(' · ')}
        lines={delivery}
      />
      <InfoCard
        icon="credit-card"
        title={t('text158')}
        lines={[
          [info.payment_type?.name, info.paid ? account.paid : ''].filter(Boolean).join(' · '),
          document?.number ? `${t('text151')} ${document.number}` : '',
        ]}
      >
        {payable ? (
          <a href={info.payment_url} className={cn(TEXT_LINK, 'w-fit')}>
            {t('text207')}
          </a>
        ) : null}
      </InfoCard>
      <InfoCard
        icon="receipt"
        title={t('text45')}
        lines={[
          extras.company,
          name,
          joinStreet(info.address, info.street_number),
          placeLine(info.postcode, info.city, info.country),
          extras.ico ? `${t('text42')}: ${extras.ico}` : '',
          extras.dic ? `${t('text43')}: ${extras.dic}` : '',
          extras.icDph ? `${t('text44')}: ${extras.icDph}` : '',
        ]}
      />
    </div>
  );
}
